import React, { useEffect, useRef, useState } from 'react';

import './index.module.less';

import Filter from '@helpers/Filter';
import BrowserHistory from '@helpers/BrowserHistory';
import { paths, deviceAuthStatus, deviceStatus } from '@helpers/Constants';
import { Api } from '@apis/client';
import Http from '@helpers/Http';
import userStore from '@stores/user';
import { DeviceBindUserInfo } from '@apis/device/getDeviceDetailInfo';
import FridayService from '@services/friday';

import NavBar from '@components/NavBar';
import CommonBtn from '@views/components/CommonBtn';
import ConfirmModal, { Confirm } from '@views/components/ConfirmModal';
import Toast from '@widgets/Toast';

const DeviceInfoEntry = () => {
  const [detail, setDetail] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showEditPhone, setShowEditPhone] = useState(false);
  const params = useRef(Filter.urlParams(window.location.search));

  const unbind = async () => {
    const condition = {
      snCode: detail.snCode,
      company: detail.company,
      phone: detail.phone,
    };
    await Http.post(Api.PostDeviceUnbind, condition);
    setTimeout(() => {
      Toast.show({
        title: '解绑成功',
        type: 'strip',
      });
      BrowserHistory.goBack();
    }, 100);
  };
  const confirm = async () => {
    const { data } = await Http.post(Api.GetCardInfoBySncode, {
      snCode: detail.snCode,
      company: detail.company,
    });

    if(data?.status === deviceStatus.forbid) {
      ConfirmModal.show({
        title: '设备已禁用',
        text: '设备已被服务商禁用，请联系服务商解除。',
        confirmBtnText: '我知道了',
        confirmBtnStyle: { backgroundColor: '#F2F4F7', color: '#55585C'},
      });
      return
    }

    //判断设备是否在线
    if (data.isOnLine) {
      ConfirmModal.show({
        title: '确定解除绑定？',
        text: '解绑后，学生证上的数据将被删除。',
        showCancelBtn: true,
        confirmTheme: 'warn',
        confirmBtnText: '解除绑定',
        onConfirm: () => {
          unbind();
        },
      });
    } else {
      ConfirmModal.show({
        title: '确定解除绑定？',
        text:
          '当前学生证处于离线状态，若解除绑定，存在设置信息泄漏的风险。建议学生证在线后再解绑。',
        showCancelBtn: true,
        cancelBtnStyle: {
          color: '#E52E3D',
          background: '#FFEBEB',
        },
        confirmBtnText: '暂不解绑',
        cancelBtnText: '仍解除绑定',
        onConfirm: () => {},
        onCancel: (cancelType: string) => {
          if (cancelType === 'cancel') {
            unbind();
          }
        },
      });
    }
  };

  const toApplyDetail = async () => {
    const { data } = await Http.post(Api.GetCardInfoBySncode, {
      snCode: detail.snCode,
      company: detail.company,
    });
    if(data?.status === deviceStatus.forbid) {
      ConfirmModal.show({
        title: '设备已禁用',
        text: '设备已被服务商禁用，请联系服务商解除。',
        confirmBtnText: '我知道了',
        confirmBtnStyle: { backgroundColor: '#F2F4F7', color: '#55585C'},
      });
      return
    }

    BrowserHistory.push(
      `${PUBLIC_URL}${paths.deviceInfoAuthorization}?sncode=${detail.snCode}`,
    );
  };

  const getDeviceInfo = async () => {
    const { data } = await Http.post(Api.GetDeviceDetailInfo, {
      snCode: params.current.sncode,
      company: 'lingge',
    });
    setDetail(data);
    setPhoneNumber(data?.phone || '');
  };

  const changeBindPhone = () => {
    FridayService.addEvent('device_information_bind_phone_click');
    setShowEditPhone(true);
  };

  const inputText = (event: React.FocusEvent<HTMLInputElement>) => {
    setPhoneNumber(event.target.value);
  };

  const isEmpty = (value: string) => {
    return !value || value.length === 0;
  };

  const toQrcodePage = () => {
    FridayService.addEvent('device_information_device_qr_code_click');
    BrowserHistory.push(
      `${PUBLIC_URL}${paths.deviceQrcodeInfo}?sncode=${params.current.sncode}`,
    );
  };

  const confirmChangeBindPhone = () => {
    FridayService.addEvent(
      'device_information_bind_phone_confirm_button_click',
    );
    const regexNumber = new RegExp('^[0-9]+$');
    setShowEditPhone(false);
    setTimeout(async () => {
      if (isEmpty(phoneNumber)) {
        return Toast.show({
          type: 'strip-bottom',
          title: '请输入手机号',
        });
      } else if (phoneNumber.length < 11 || !regexNumber.test(phoneNumber)) {
        return Toast.show({
          type: 'strip-bottom',
          title: '请输入 11 位数字',
        });
      } else {
        await Http.post(Api.PostDevicePhoneNumber, {
          snCode: detail.snCode,
          company: detail.company,
          phone: phoneNumber,
        });
        getDeviceInfo();
      }
    }, 50);
  };

  useEffect(() => {
    FridayService.addEvent('device_information_self_show');
    getDeviceInfo();
  }, []);

  const getStatusText = () => {
    let text = '未授权';
    switch (detail?.powerStatus) {
      case deviceAuthStatus.allAuth:
        text = '已授权';
        break;
      case deviceAuthStatus.noAuth:
        text = '未授权';
        break;
      case deviceAuthStatus.partAuth:
        text = '部分授权';
        break;
    }
    return text;
  };

  const renderPhone = (phone: string) => {
    return phone
      ? phone.toString().replace(/(\d{3})\d*(\d{4})/, `$1****$2`)
      : '****';
  };

  const checkCurrentUserAdminAuth = () => {
    let isAdmin = false;
    if (!detail.users?.length) {
      return false;
    }
    const user = detail.users?.find(
      (i: DeviceBindUserInfo) => i.userUid === userStore.userInfo.uid,
    );
    if (user && user.isAdmin) {
      isAdmin = true;
    }
    return isAdmin;
  };

  if (!detail) {
    return null;
  }

  const isAdmin = checkCurrentUserAdminAuth();
  return (
    <div className="flex container" styleName="container">
      <NavBar title="设备信息" />
      <div styleName="content">
        <div className="flex" styleName="rows">
          <div styleName="row-item" className="flex">
            <div styleName="row-item-title">设备名称</div>
            <div
              className="els"
              styleName="row-item-text"
              style={{ maxWidth: '60%' }}
            >
              {detail?.userName}的学生证
            </div>
          </div>
          <div styleName="row-item" className="flex" onClick={changeBindPhone}>
            <div styleName="row-item-title">绑定手机</div>
            <div styleName="row-item-text">
              {detail?.phone ? renderPhone(detail?.phone) : '未绑定'}
              <i className="iconfont icon-icon-action-arrow" />
            </div>
          </div>
          <div styleName="row-item" className="flex">
            <div styleName="row-item-title">设备号</div>
            <div styleName="row-item-text"> {detail?.snCode} </div>
          </div>
          <div styleName="row-item" className="flex" onClick={toQrcodePage}>
            <div styleName="row-item-title">设备二维码</div>
            <div styleName="row-item-text">
              <i
                className="iconfont icon-erweima"
                style={{ fontSize: '.36rem' }}
              />
              <i className="iconfont icon-icon-action-arrow" />
            </div>
          </div>
        </div>
        <div styleName="row-single">
          {detail.isSchoolAuthorized ? (
            <div styleName="row-item" className="flex" onClick={toApplyDetail}>
              <div styleName="row-item-title">对学校授权</div>
              <div styleName="row-item-text">
                <span>{getStatusText()}</span>
                <i className="iconfont icon-icon-action-arrow" />
              </div>
            </div>
          ) : null}
        </div>
        <div styleName="row-single">
          <div styleName="row-item" className="flex">
            <div styleName="row-item-title">固件版本</div>
            <div
              styleName="row-item-text"
              style={{
                width: 'auto',
                maxWidth: '70%',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                textAlign: 'left',
              }}
            >
              {detail?.firmwareCode || ''}
            </div>
          </div>
        </div>
      </div>
      {isAdmin ? (
        <CommonBtn type="fixed" btnText="解除绑定" onConfirm={confirm} />
      ) : null}
      {showEditPhone ? (
        <Confirm
          title="编辑绑定手机号"
          ContentWidget={
            <div styleName="phone-card-text">
              <input
                placeholder="请输入手机号"
                maxLength={11}
                value={phoneNumber}
                onChange={inputText}
              />
              {phoneNumber?.length ? (
                <i
                  className="iconfont icon-cuowu"
                  onClick={() => setPhoneNumber('')}
                />
              ) : null}
            </div>
          }
          disabled={!phoneNumber?.length}
          showCancelBtn
          onCancel={() => {
            setShowEditPhone(false);
            setPhoneNumber(detail.phone);
          }}
          onConfirm={confirmChangeBindPhone}
        />
      ) : null}
    </div>
  );
};

export default DeviceInfoEntry;
